<template>
  <div class="section-card">
    <div class="section-header">
      <p class="section-title">Quotations ({{ quotes.length }})</p>
    </div>
    <div class="mb-4">
      
      <app-button-flat @click="handleAddQuotation" block variant="outlined">
        <v-icon size="18">mdi-plus</v-icon> New Quotation
      </app-button-flat>
    </div>
    <!-- <v-divider></v-divider> -->
    <div v-if="!quotes.length" class="empty-state">
      <v-icon size="20" class="empty-state-icon">mdi-file-document-outline</v-icon>
      <p class="empty-state-text">Quotations have not been received yet.</p>
    </div>
    <div v-else class="quote-list">
      <div v-for="quote in quotes" :key="String(quote.id)" class="quote-card" :class="{ 'quote-card--clickable': true }"
        @click="handleOpenQuote(quote.id)">
        <div class="quote-main">
          <div class="quote-icon icon-bg" :class="quote.iconTone">
            <v-icon size="18">mdi-file-check-outline</v-icon>
          </div>
          <div class="quote-copy">
            <p class="quote-title">{{ quote.quoteNo }}</p>
            <div class="quote-meta">
              <span class="quote-price">{{ quote.price }}</span>
              <span class="quote-dot"></span>
              <span class="">{{ quote.date }}</span>
            </div>
            <!-- <div class="quote-meta">
              <span class="quote-subtitle">{{ quote.quoteNo }}</span>
            </div> -->
          </div>
        </div>
        <div class="quote-side">
          <v-chip label :color="getStatusColor(quote.status)" size="small">{{ quote.status }}</v-chip>
          <v-icon size="18" class="quote-chevron">mdi-chevron-right</v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { getStatusColor } from "@/shared/utils/utils";
import { useRouter } from "vue-router";
import { useModalStore } from "@/shared/stores/modal";
import AddQuotationModal from "../quotation-detail/AddQuotationModal.vue";
import QuotationOptionsModal from "../quotation-detail/QuotationOptionsModal.vue";

type QuoteItem = {
  id: number | string;
  quoteNo: string;
  title: string;
  price: string;
  date: string;
  status: string;
  icon: string;
  iconTone: string;
};

const router = useRouter();
const modal = useModalStore();

const props = withDefaults(
  defineProps<{
    quotes?: QuoteItem[];
    inquiryId?: string | number;
  }>(),
  {
    quotes: () => [],
    inquiryId: "",
  },
);

function handleOpenQuote(id: number | string) {
  router.push({
    name: "vendor.quotations.detail",
    params: { id: String(id) },
  });
}

function handleAddQuotation() {
  modal.open(QuotationOptionsModal, {
    inquiryId: props.inquiryId
  }, {
    fullscreen: true,
    showHeader: false,
    title: "Create Quotation",
  });
}
</script>

<style scoped>
.section-card {
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.section-title {
  margin: 0;
  font-size: var(--font-size-default);
  letter-spacing: 0.1em;
  text-transform: capitalize;
  color: var(--color-title);
  font-weight: 700;
}

.quote-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 68px;
  border: 1px dashed #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
  color: #64748b;
}

.empty-state-icon {
  color: #94a3b8;
}

.empty-state-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.quote-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
}

.quote-card:not(:last-child) {
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
}



.quote-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.quote-icon {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}


.quote-copy {
  min-width: 0;
  flex: 1;
}

.quote-title {
  margin: 0;
  font-weight: 600;
  font-size: var(--font-size-default);
  color: #0f172a;
  min-width: 0;
}

.quote-subtitle {
  color: #9a8e86;
  font-weight: 600;
  font-size: var(--font-size-default);
}

.quote-meta {
  margin-top: 4px;
  font-size: var(--font-size-default);
  color: var(--color-subtitle-light);
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.quote-price {
  color: var(--color-theme);
  font-weight: 600;
  font-size: 14px;
}

.quote-status {
  font-size: var(--font-size-default);
  font-weight: 500;
  background: #f3dd8e;
  padding: 5px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.quote-side {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}

.quote-dot {
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: #bcaea5;
}

.quote-chevron {
  color: #8c97a4;
}
</style>
